import classNames from 'classnames'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Flex } from 'core-components/library/flex'
import { Input } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'
import { getWidgetMeta } from 'core-components/sitebuilder/helpers/MetaSelector'
import { useColumnSizeData } from 'core-components/sitebuilder/layout/flexItem/hooks/useColumnSizeData'
import {
  IRenderCustomContentParams,
  IRenderCustomSettingsParams,
} from 'core-components/sitebuilder/layout/flexItem/models/IRenderCustomContent'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { IFlexItem } from 'core-components/sitebuilder/models/flexGrid/IFlexItem'
import { IFlexLayout } from 'core-components/sitebuilder/models/flexGrid/IFlexLayout'
import { IFlexLayoutProps } from 'core-components/sitebuilder/models/flexGrid/IFlexLayoutProps'
import { ISystemWidgetSettings } from 'core-components/sitebuilder/models/flexGrid/ISystemWidgetSettings'
import { IPageWidget } from 'core-components/sitebuilder/models/IWidget'
import { WidgetSettings } from 'core-components/sitebuilder/settings/WidgetSettings'
import { FlexWrapperHeaderSettings } from 'core-components/sitebuilder/settings/wrapper/flexWrapperHeaderSettings/FlexWrapperHeaderSettings'
import { selectEditLayoutMode } from 'core-components/sitebuilder/store/selectors/selectEditLayoutMode'
import { selectRouteParams } from 'core-components/sitebuilder/store/selectors/selectRouteParams'
import { selectWidgets } from 'core-components/sitebuilder/store/selectors/selectWidgets'

import { AbsolutePositionSelector } from './flexItem/AbsolutePositionSelector'
import { WidgetsListConfiguratorList } from './flexItem/ConfiguratorList'
import { clearTimeouts, IFlexItemTimeouts } from './flexItem/helpers/clearTimeouts'
import { getDirectionBreakByClass } from './flexItem/helpers/getDirectionBreakByClass'
import { getSizeByWidth } from './flexItem/helpers/getSizeByWidth'
import { PinOutPortal } from './flexItem/PinOutPortal'
import { PinOutContext } from './flexItem/pinOutPortal/context/PinOutContext'
import { SizeSelector } from './flexItem/SizeSelector'
import { WidgetItem } from './flexItem/WidgetItem'
import { downloadFileWithData } from './helpers/downloadFileWithData'

import styles from './FlexItem.module.scss'

interface IFlexItemProp {
  itemLayoutSet: IFlexLayout
  parent: any
  allowEdit: boolean
  classes: {
    hasSettings: string
    headerSettings: string
    controlElement: string
    isDrop: string
    widgets: string
    canDrop: string
    column: string
    columnNoGrow: string
  }
  item: IFlexItem
  setState: (newItem: IFlexItem | undefined, prevName?: string) => void
  id: string
  flexLayoutClass: (props: IFlexLayoutProps) => ReactElement
  widgetParams: Record<string, any>
  alignTop: boolean
  parentRefs: any[]
  resize: number
  parentResized: boolean
  onResize: () => void
  page: string
  area: string
  onTogglePanelChange: (value?: boolean) => void
  togglePanelOpen: boolean
  onNameChange: (item: IFlexItem, newName: string) => void
  onPositionChange: (item: IFlexItem, direction: 'up' | 'down') => void
  renderCustomSettingsWidget?: (data: IRenderCustomSettingsParams) => ReactElement | null
  renderCustomWidget?: (data: IRenderCustomContentParams) => ReactElement | null
}

export const FlexItem = (props: IFlexItemProp) => {
  const routeParams = useSelector(selectRouteParams)
  const widgets = useSelector(selectWidgets)
  const showLayout = useSelector(selectEditLayoutMode)
  const location = useLocation()

  const [moveWidget, setMoveWidget] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [pinOut, setPinOut] = useState(false)
  const [isVisible, setIsVisible] = useState<boolean>(true)
  const [openedWidget, setOpenedWidget] = useState('')
  const [autoWidth, setAutoWidth] = useState(false)
  const [widgetVisibility, setWidgetVisibility] = useState(false)
  const [domLoaded, setDomLoaded] = useState(false)
  const [lastItemSize, setLastItemSize] = useState('')
  const [isFullWidth, setFullWidth] = useState<boolean>(false)
  const [resizeCounter, setResizeCounter] = useState<number>(1)
  const [parentResizeCounter, setParentResizeCounter] = useState<number>(1)

  const { stateColSize, setColSize, columnSize, columnClasses } = useColumnSizeData({
    itemSize: props.item.size,
  })

  const widgetsRef = useRef<HTMLDivElement>(null)
  const firstResizedRef = useRef<boolean>(false)
  const timeoutsRef = useRef<IFlexItemTimeouts>({})

  const { setState: setStateProps, onTogglePanelChange, item } = props
  const blockAutoResize = !!(item.systemWidgetSettings && item.systemWidgetSettings.blockAutoResize)
  const blockSticky = !!item.systemWidgetSettings?.blockSticky
  const isAbsolutePosition = !!item.systemWidgetSettings?.isAbsolutePosition
  const appAbsoluteRoot = document.getElementById('absolute_container')

  const resizeAction = () => {
    const ref = props.parentRefs[props.id]
    if (ref && props.parent && (props.parentResized || !firstResizedRef.current)) {
      const parent = props.parent
      const pWidth: number = parent.offsetWidth
      const colValue = blockAutoResize && parent.className.toString().match(/col-\d+/)
      const size =
        blockAutoResize && colValue && colValue[0]
          ? getDirectionBreakByClass(colValue[0])
          : getSizeByWidth(pWidth)

      setFullWidth(document.body.clientWidth === ref.offsetWidth)

      if (pWidth <= ref.offsetWidth && size !== stateColSize) {
        setColSize(size)
        clearTimeouts(timeoutsRef.current)
        timeoutsRef.current.resizeChild = window.setTimeout(() => {
          timeoutsRef.current.resizeChild = undefined
          setColSize(size)
          setParentResizeCounter(() => {
            firstResizedRef.current = true
            return props.resize
          })
        }, 0)
      } else if (props.resize !== parentResizeCounter) {
        clearTimeouts(timeoutsRef.current)
        timeoutsRef.current.resizeChild2 = window.setTimeout(() => {
          timeoutsRef.current.resizeChild2 = undefined
          setParentResizeCounter(() => {
            firstResizedRef.current = true
            return props.resize
          })
        }, 0)
      } else if (!firstResizedRef.current) {
        clearTimeouts(timeoutsRef.current)
        timeoutsRef.current.resizeChild3 = window.setTimeout(() => {
          timeoutsRef.current.resizeChild3 = undefined
          firstResizedRef.current = true
        }, 0)
      }
    }
  }

  const resetSizes = useCallback(() => {
    firstResizedRef.current = false
    setColSize(undefined)
    setResizeCounter(1)
    setParentResizeCounter(1)
  }, [])

  const onToggleAutoWidth = useCallback(
    (bool?: boolean) => {
      const newValue = bool === undefined ? !autoWidth : bool

      if (autoWidth !== bool) {
        setAutoWidth(newValue)
        onTogglePanelChange(autoWidth)
      }
    },
    [autoWidth, onTogglePanelChange]
  )

  const systemWidgetChangeHandler = useCallback(
    (newData: Partial<ISystemWidgetSettings>) => {
      const newItem = {
        ...item,
        systemWidgetSettings: {
          ...item.systemWidgetSettings,
          ...newData,
        },
      }
      setStateProps({
        ...newItem,
      })
    },
    [item, setStateProps]
  )

  useCallback(() => {
    firstResizedRef.current = false
  }, [item.size, props.resize, showLayout, props.parentResized, props.togglePanelOpen])

  useEffect(resetSizes, [resetSizes, location])

  useEffect(() => {
    const finded = item.widgets.length && widgets.find((w) => w.i === item.widgets[0].id)
    if (finded) {
      const res = localStorage.getItem('pin_out_widgets')
      if (res) {
        const obj = JSON.parse(res)
        if (obj[finded.i] && obj[finded.i].pinOut) {
          setPinOut(true)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (widgetsRef.current && props.parent) {
      !domLoaded && setDomLoaded(true)
    }
  })

  useEffect(() => {
    if (!showLayout && widgetsRef.current) {
      props.parentRefs[props.id] = widgetsRef.current
    }
  }, [widgetsRef.current, item])

  useEffect(() => {
    if (lastItemSize !== item.size) {
      setLastItemSize(item.size)
      if (stateColSize) {
        setColSize(undefined)
      }
    }
    if (!showLayout) {
      clearTimeouts(timeoutsRef.current)
      timeoutsRef.current.resize = window.setTimeout(() => {
        timeoutsRef.current.resize = undefined
        resizeAction()
      }, 0)
    }
  }, [
    domLoaded,
    stateColSize,
    resizeCounter,
    item,
    lastItemSize,
    props.parentRefs,
    props.parent,
    props.parentResized,
  ])

  useEffect(() => {
    // if (lastItemSize !== item.size) {
    //   if (!lastItemSize) {
    //     return setLastItemSize(item.size)
    //   }
    //   setLastItemSize(item.size)
    //   if (columnSize) {
    //     setColSize(undefined)
    //   }
    // }
  }, [lastItemSize, item.size, item])

  useEffect(() => {
    if (!props.parentResized) {
      return
    }
    if (stateColSize) {
      clearTimeouts(timeoutsRef.current)
      timeoutsRef.current.clearSize = window.setTimeout(() => {
        timeoutsRef.current.clearSize = undefined
        setColSize(undefined)
      }, 0)
    } else {
      clearTimeouts(timeoutsRef.current)
      timeoutsRef.current.setNewSize = window.setTimeout(() => {
        timeoutsRef.current.setNewSize = undefined
        setResizeCounter(resizeCounter + 1)
      }, 0)
    }
  }, [item.size, props.resize, showLayout, props.parentResized, props.togglePanelOpen])

  if (item.isFlexLayout && item.layoutSet) {
    const FlexLayout = props.flexLayoutClass
    return (
      <FlexLayout
        renderCustomSettingsWidget={props.renderCustomSettingsWidget}
        renderCustomWidget={props.renderCustomWidget}
        onTogglePanelChange={onTogglePanelChange}
        togglePanelOpen={props.togglePanelOpen}
        onResize={props.onResize}
        resize={parentResizeCounter}
        parentResized={firstResizedRef.current}
        rootRef={widgetsRef}
        parent={props.parent}
        alignTop={props.alignTop}
        allowEdit={props.allowEdit}
        width={columnSize}
        fullWidth={isFullWidth}
        classNameLayout={classNames(columnClasses, props.classes.column)}
        widgetParams={props.widgetParams}
        visibleSettings={item.layoutSet}
        allSettings={props.itemLayoutSet}
        page={props.page}
        area={props.area}
        flexItem={item}
        updateFlexLayout={(newSet) => {
          setStateProps({
            ...item,
            layoutSet: newSet,
          })
        }}
        additionalHead={
          <FlexWrapperHeaderSettings
            item={item}
            columnSize={columnSize}
            updateItem={(newFlexItem) => setStateProps(newFlexItem)}
          />
        }
      />
    )
  }

  // ### Widgets Area

  let propsWidgetsFromRoutes = {
    ...item.customWidgetSettings,
    name: item.name,
  }
  let allowPinOut: boolean
  if (item.name) {
    propsWidgetsFromRoutes['nameValue'] = routeParams[item.name]
  }
  const finded =
    item.widgets && item.widgets.length && widgets.find((w) => w.i === item.widgets[0].id)

  if (item.widgets && item.widgets.length > 0) {
    if (item.systemWidgetSettings) {
      allowPinOut = !!item.systemWidgetSettings.allowPinOut
      if (finded && finded.type === 'Iframe') {
        propsWidgetsFromRoutes = {
          ...propsWidgetsFromRoutes,
          ...routeParams,
        }
      } else {
        item.systemWidgetSettings.routes &&
          item.systemWidgetSettings.routes.forEach((rInfo) => {
            if (routeParams[rInfo.paramName]) {
              propsWidgetsFromRoutes[rInfo.propName || rInfo.paramName] =
                routeParams[rInfo.paramName]
            }
          })
      }
    }
  }

  const selectWidgetButton = () => (
    <div className={styles['widget-placeholder']}>
      <Button
        className={styles['widget-placeholder__button']}
        color={'grey'}
        variant={'text'}
        onClick={() => setOpenModal(true)}
      >
        Click to select widget
      </Button>
    </div>
  )

  const widgetMeta = (finded && getWidgetMeta(finded.i)) || undefined
  if (!item.widgets) {
    item.widgets = []
  }
  const widgetsMarkup =
    !finded && item.widgets?.length ? (
      selectWidgetButton()
    ) : (
      <div className={classNames(props.classes.widgets)}>
        {!item.widgets || item.widgets.length === 0 ? selectWidgetButton() : ''}
        {item.widgets.map((pageWidget, index) => {
          const foundWidget = widgets.find((w) => w.i === pageWidget.id)

          if (!foundWidget) {
            return null
          }

          const obj = JSON.parse(window.localStorage.getItem('pin_out_widgets') || '{}')[
            pageWidget.id
          ]
          const hidePinOut = obj?.hidePinOut
          const autoHeight = pageWidget.height === 'auto'
          const height =
            pageWidget.height === 'custom' ? `${pageWidget.customHeight}px` : pageWidget.height
          return (
            <div
              key={pageWidget.id}
              className={classNames(
                styles.widget_root,
                !isVisible ? 'content-invisible' : '',
                !autoHeight ? styles['widget_restrictHeight'] : ''
              )}
              style={{
                width: isVisible ? '100%' : '60px',
                height: autoHeight ? '100%' : height,
              }}
            >
              <>
                <PinOutContext.Provider
                  value={{
                    moveWidget: (move: boolean) => setMoveWidget(move),
                    setPinOut: () => {
                      const localpin = !pinOut
                      if (localpin) {
                        const res = localStorage.getItem('pin_out_widgets')
                        const res2 = JSON.parse(res || '{}')
                        localStorage.setItem(
                          `pin_out_widgets`,
                          JSON.stringify({
                            ...res2,
                            [pageWidget.id]: {
                              pinOut: localpin,
                              hidePinOut: '',
                              top: '',
                              left: '',
                            },
                          })
                        )
                        setPinOut(localpin)
                      } else {
                        const res = localStorage.getItem('pin_out_widgets')
                        const res2 = JSON.parse(res || '{}')
                        delete res2[pageWidget.id]
                        localStorage.setItem(
                          'pin_out_widgets',
                          JSON.stringify({
                            ...res2,
                          })
                        )
                        props.onResize()
                        setPinOut(localpin)
                      }
                    },
                    onRmvPlaceHolder: () => {
                      const res = localStorage.getItem('pin_out_widgets')
                      const res2 = JSON.parse(res || '{}')
                      const currentItem = res2[pageWidget.id]
                      if (currentItem) {
                        localStorage.setItem(
                          `pin_out_widgets`,
                          JSON.stringify({
                            ...res2,
                            [pageWidget.id]: {
                              ...currentItem,
                              hidePinOut: item.name,
                            },
                          })
                        )
                        props.onResize()
                      }
                    },
                  }}
                >
                  <PinOutPortal
                    allowPinOut={allowPinOut}
                    pinOut={pinOut}
                    moveWidget={moveWidget}
                    setPinOut={(arr: boolean) => setPinOut(arr)}
                    widgetId={pageWidget.id}
                    appAbsoluteRoot={appAbsoluteRoot}
                    hidePinOut={hidePinOut}
                  >
                    <WidgetItem
                      fullWidth={isFullWidth}
                      key={`${pageWidget.id}-${JSON.stringify({
                        w: props.widgetParams,
                      })}`}
                      pageWidget={pageWidget}
                      widgetId={foundWidget.i}
                      height={pageWidget.height}
                      isFlex={true}
                      widgetWidth={columnSize}
                      allowEdit={props.allowEdit}
                      allowPinOut={allowPinOut}
                      pinOut={pinOut}
                      previewMode={
                        widgetMeta?.isFlexGridControlElement
                          ? false
                          : !widgetVisibility && showLayout
                      }
                      widgetParams={{ ...props.widgetParams }}
                      routeParams={propsWidgetsFromRoutes}
                      systemWidgetSettings={item.systemWidgetSettings}
                      onClickEdit={() => {
                        setOpenedWidget(pageWidget.id)
                      }}
                      onToggleColumnWidth={onToggleAutoWidth}
                      onExportSettings={() => {
                        downloadFileWithData(item, `${foundWidget.i}.json`)
                      }}
                      onImportSettings={(item) => {
                        setStateProps(item, props.item.name)
                      }}
                      onChangeCustomSettings={(customWidgetSettings: unknown) => {
                        setStateProps({
                          ...item,
                          customWidgetSettings: customWidgetSettings as object,
                        })
                      }}
                      onChangeScale={(newScale: number) => {
                        setStateProps({
                          ...item,
                          widgets: [
                            {
                              ...item.widgets[0],
                              scale: newScale,
                            },
                          ],
                        })
                      }}
                      onClickRemove={() => {
                        item.widgets.splice(index, 1)
                        setStateProps({
                          ...item,
                          widgets: item.widgets.slice(),
                        })
                      }}
                      renderCustomWidget={props.renderCustomWidget}
                    />
                  </PinOutPortal>
                </PinOutContext.Provider>
                {openedWidget === pageWidget.id ? (
                  <WidgetSettings
                    renderCustomSettingsWidget={props.renderCustomSettingsWidget}
                    onClose={() => setOpenedWidget('')}
                    onChangeAndClose={(
                      pageSettingsWidget,
                      systemWidgetSettings,
                      redactorSettings
                    ) => {
                      setOpenedWidget('')
                      if (pageSettingsWidget) {
                        setStateProps({
                          ...item,
                          systemWidgetSettings: systemWidgetSettings || {},
                          customWidgetSettings: redactorSettings || item.customWidgetSettings,
                          widgets: [{ ...item.widgets[0], ...pageSettingsWidget }],
                        })
                      }
                    }}
                    widget={foundWidget}
                    pageWidget={pageWidget}
                    title="Widget settings"
                    additionalSettings={item.systemWidgetSettings}
                    hasAdditionalSettings
                  />
                ) : null}
              </>
            </div>
          )
        })}
      </div>
    )

  return showLayout ? (
    <div
      className={classNames(props.classes.column, props.classes.columnNoGrow, columnClasses)}
      data-testid={`flexItemRoot_${props.item.name}`}
      data-id={props.item.name}
      key={props.id}
    >
      <WidgetsListConfiguratorList
        renderCustomSettingsWidget={props.renderCustomSettingsWidget}
        open={openModal}
        widgets={widgets}
        onUpdatePageWidgetsData={(widgetsData) => {
          const pageWidgetData = {
            height: 'auto',
            canStretch: true,
            alignContentTop: false,
            toggleButtonVisibility: false,
            id: widgetsData[0].i,
          } as IPageWidget

          if (item.widgets.length === 0) {
            item.widgets.push(pageWidgetData)
          } else {
            item.widgets[0] = pageWidgetData
          }

          setStateProps({
            ...item,
            widgets: item.widgets.slice(),
          })
          setOpenModal(false)
        }}
        onClose={() => setOpenModal(false)}
      />
      <div className={props.classes.headerSettings}>
        <div className={styles['flex-layout__control-element-wrapper']}>
          {!item.name && widgetMeta && widgetMeta.isFlexGridControlElement ? (
            <div className={classNames(styles['flex-layout__control-element'])}>
              <div className={styles['widget_name_wrapper']}>
                <i className={classNames(styles['attention_icon'], 'kbase kbase-warning-alt')} />
                <Input
                  value={item.name}
                  label="Set name for tab element"
                  onChange={(newName) => {
                    setStateProps({
                      ...item,
                      name: newName,
                    })
                  }}
                />
              </div>
            </div>
          ) : null}
          <Flex
            flexWrap={'wrap'}
            alignItems={'center'}
            className={classNames(props.classes.controlElement, 'margin-bottom-xs')}
          >
            <Tooltip
              className={styles['icon-btn__tooltip']}
              tipContent={() => <span>{'Change to Wrapper'}</span>}
            >
              <Button
                className={classNames(styles['icon-btn'], styles['push-right'])}
                variant={'text'}
                color={'grey'}
                iconButton={'kbase kbase-reload'}
                onClick={() => {
                  setStateProps({
                    ...item,
                    widgets: [],
                    layoutSet: {
                      items: [
                        {
                          ...item,
                          isFlexLayout: false,
                          size: FlexDirectionBreak.p212,
                        },
                      ],
                    },
                    isFlexLayout: true,
                  })
                }}
              />
            </Tooltip>
            <Tooltip
              className={styles['icon-btn__tooltip']}
              tipContent={() => <span>{'Delete Widget'}</span>}
            >
              <Button
                className={classNames(styles['icon-btn'], styles['push-right'])}
                variant={'text'}
                color={'grey'}
                iconButton={'kbase kbase-delete'}
                onClick={() => setStateProps(undefined)}
              />
            </Tooltip>
            <Tooltip
              className={styles['icon-btn__tooltip']}
              tipContent={() => <span>{'Reselect widget'}</span>}
            >
              <Button
                className={classNames(styles['icon-btn'], styles['push-right'])}
                variant={'text'}
                color={'grey'}
                iconButton={'kbase kbase-edit'}
                onClick={() => setOpenModal(true)}
              />
            </Tooltip>
            <Checkbox
              padding
              label="Auto resize"
              checked={blockAutoResize}
              onChange={(value) => {
                systemWidgetChangeHandler({ blockAutoResize: value })
              }}
            />
            <Checkbox
              className={styles.checkbox_spacing}
              padding
              label="Sticky"
              checked={blockSticky}
              onChange={(value) => {
                systemWidgetChangeHandler({ blockSticky: value })
              }}
            />

            <Checkbox
              className={styles.checkbox_spacing}
              padding
              label="Absolute position"
              checked={isAbsolutePosition}
              onChange={(value) => {
                systemWidgetChangeHandler({ isAbsolutePosition: value })
              }}
            />

            {isAbsolutePosition ? (
              <AbsolutePositionSelector
                value={item.systemWidgetSettings.absolutePosition}
                onChange={systemWidgetChangeHandler}
              />
            ) : null}
          </Flex>
        </div>
        <div className={styles['flex-layout__control-element-wrapper-right']}>
          <Tooltip
            className={styles['icon-btn__tooltip']}
            tipContent={() => <span>{'Move up'}</span>}
          >
            <Button
              className={classNames(styles['icon-btn'], styles['push-right'])}
              variant={'text'}
              color={'grey'}
              iconButton={'kbase kbase-omni-chevron-up'}
              onClick={() => props.onPositionChange(item, 'up')}
            />
          </Tooltip>

          <Tooltip
            className={styles['icon-btn__tooltip']}
            tipContent={() => <span>{'Move down'}</span>}
          >
            <Button
              className={classNames(styles['icon-btn'], styles['push-right'])}
              variant={'text'}
              color={'grey'}
              iconButton={'kbase kbase-omni-chevron-down'}
              onClick={() => props.onPositionChange(item, 'down')}
            />
          </Tooltip>

          {widgetMeta && !widgetMeta.isFlexGridControlElement ? (
            <Tooltip
              className={styles['icon-btn__tooltip']}
              tipContent={() => <span>{'Change visibility'}</span>}
            >
              <Button
                className={classNames(styles['icon-btn'], styles['push-right'])}
                variant={'text'}
                color={'grey'}
                iconButton={widgetVisibility ? 'kbase kbase-lvl-eye' : 'kbase kbase-lvl-eye-off'}
                onClick={() => setWidgetVisibility(!widgetVisibility)}
              />
            </Tooltip>
          ) : null}

          <SizeSelector item={item} value={columnSize} onChange={setStateProps} />
        </div>
      </div>
      {widgetsMarkup}
    </div>
  ) : finded ? (
    <div
      ref={widgetsRef}
      key={props.id}
      data-id={props.item.name}
      className={classNames(
        props.classes.column,
        blockSticky ? styles.sticky : '',
        isAbsolutePosition
          ? styles[`absolute-${item.systemWidgetSettings.absolutePosition || 'right'}`]
          : '',
        autoWidth ? styles.autoWidth : columnClasses,
        styles.widget_wrapper,
        item.widgets && item.widgets[0] && !item.widgets[0].canStretch
          ? props.classes.columnNoGrow
          : ''
      )}
    >
      {widgetsMarkup}
      {item.widgets && item.widgets[0] && item.widgets[0].toggleButtonVisibility ? (
        <>
          <Button
            className={styles.toggleButton}
            iconButton={`kbase kbase-arrow-${isVisible ? 'left' : 'right'}`}
            variant={'outlined'}
            onClick={() => {
              onToggleAutoWidth(isVisible)
              setIsVisible(!isVisible)
            }}
          />
          <div className={styles.toggleButton_line}></div>
        </>
      ) : null}
    </div>
  ) : null
}
